import React from 'react';
import {
  FileText,
  Mail,
  ClipboardList,
  ClipboardCheck,
  Shield,
  HelpCircle,
  Share2,
  Briefcase,
  Receipt,
  Tag,
  Users,
  UserCheck,
  Globe,
  MessageSquare,
  LayoutDashboard,
  Rocket,
  UserCircle,
  CalendarClock,
  ListChecks,
} from 'lucide-react';

export type IconType = React.ComponentType<{ className?: string }>;

export interface AdminNavItem {
  key: string;
  labelKey: string;
  descKey?: string;
  href: string;
  icon: IconType;
  color?: string;
  requiredRole?: 'super_admin';
  match?: (pathname: string) => boolean;
}

export interface AdminNavGroup {
  key: string;
  labelKey: string;
  icon: IconType;
  items: AdminNavItem[];
}

export const ADMIN_DASHBOARD_ITEM: AdminNavItem = {
  key: 'dashboard',
  labelKey: 'admin.dashboard',
  href: '/admin',
  icon: LayoutDashboard,
  match: (pathname) => pathname === '/admin',
};

export const ADMIN_NAV_GROUPS: AdminNavGroup[] = [
  {
    key: 'content',
    labelKey: 'admin.navGroups.content',
    icon: FileText,
    items: [
      {
        key: 'posts',
        labelKey: 'admin.posts',
        descKey: 'admin.postsDesc',
        href: '/admin/posts',
        icon: FileText,
        color: 'bg-blue-500',
      },
      {
        key: 'newsletter',
        labelKey: 'admin.newsletter',
        descKey: 'admin.newsletterDesc',
        href: '/admin/newsletter',
        icon: Mail,
        color: 'bg-cyan-500',
      },
      {
        key: 'faq',
        labelKey: 'admin.faq',
        descKey: 'admin.faqDesc',
        href: '/admin/faq',
        icon: HelpCircle,
        color: 'bg-amber-500',
      },
      {
        key: 'testimonials',
        labelKey: 'admin.testimonials',
        descKey: 'admin.testimonialsDesc',
        href: '/admin/testimonials',
        icon: MessageSquare,
        color: 'bg-teal-500',
      },
      {
        key: 'socialMedia',
        labelKey: 'admin.socialMedia',
        descKey: 'admin.socialMediaDesc',
        href: '/admin/redes-sociais',
        icon: Share2,
        color: 'bg-pink-500',
      },
      {
        key: 'landingPages',
        labelKey: 'admin.landingPages',
        descKey: 'admin.landingPagesDesc',
        href: '/admin/landing-pages',
        icon: Rocket,
        color: 'bg-rose-500',
      },
      {
        key: 'quizLeads',
        labelKey: 'admin.quizLeads',
        descKey: 'admin.quizLeadsDesc',
        href: '/admin/landing-pages/leads',
        icon: ListChecks,
        color: 'bg-orange-500',
      },
      {
        key: 'avatars',
        labelKey: 'admin.avatars',
        descKey: 'admin.avatarsDesc',
        href: '/admin/avatars',
        icon: UserCircle,
        color: 'bg-violet-500',
      },
      {
        key: 'agendamentos',
        labelKey: 'admin.agendamentos',
        descKey: 'admin.agendamentosDesc',
        href: '/admin/agendamentos',
        icon: CalendarClock,
        color: 'bg-indigo-500',
      },
    ],
  },
  {
    key: 'processes',
    labelKey: 'admin.navGroups.processes',
    icon: ClipboardList,
    items: [
      {
        key: 'nif',
        labelKey: 'admin.nif',
        descKey: 'admin.nifDesc',
        href: '/admin/nif',
        icon: FileText,
        color: 'bg-indigo-500',
      },
      {
        key: 'visaAnalysis',
        labelKey: 'admin.visaAnalysis',
        descKey: 'admin.visaAnalysisDesc',
        href: '/admin/analise-visto',
        icon: ClipboardList,
        color: 'bg-teal-600',
      },
      {
        key: 'residenceAnalysis',
        labelKey: 'admin.residenceAnalysis',
        descKey: 'admin.residenceAnalysisDesc',
        href: '/admin/analise-residencia',
        icon: ClipboardCheck,
        color: 'bg-sky-600',
      },
      {
        key: 'lawyers',
        labelKey: 'admin.lawyers',
        descKey: 'admin.lawyersDesc',
        href: '/admin/advogado',
        icon: Shield,
        color: 'bg-purple-500',
      },
      {
        key: 'contract',
        labelKey: 'admin.contract',
        descKey: 'admin.contractDesc',
        href: '/admin/contrato',
        icon: FileText,
        color: 'bg-amber-600',
      },
      {
        key: 'orcamento',
        labelKey: 'admin.orcamento',
        descKey: 'admin.orcamentoDesc',
        href: '/admin/orcamento',
        icon: Receipt,
        color: 'bg-cyan-600',
      },
      {
        key: 'servicos',
        labelKey: 'admin.servicos',
        descKey: 'admin.servicosDesc',
        href: '/admin/servicos',
        icon: Tag,
        color: 'bg-lime-600',
      },
    ],
  },
  {
    key: 'relationship',
    labelKey: 'admin.navGroups.relationship',
    icon: Briefcase,
    items: [
      {
        key: 'crm',
        labelKey: 'admin.crm',
        descKey: 'admin.crmDesc',
        href: '/admin/crm/clientes',
        icon: Briefcase,
        color: 'bg-emerald-600',
        match: (pathname) =>
          pathname.startsWith('/admin/crm') ||
          pathname.startsWith('/admin/clientes') ||
          pathname.startsWith('/admin/vendas') ||
          pathname.startsWith('/admin/pagamentos'),
      },
      {
        key: 'contacts',
        labelKey: 'admin.contacts',
        descKey: 'admin.contactsDesc',
        href: '/admin/contacts',
        icon: Mail,
        color: 'bg-green-500',
      },
    ],
  },
  {
    key: 'people',
    labelKey: 'admin.navGroups.people',
    icon: Users,
    items: [
      {
        key: 'team',
        labelKey: 'admin.team',
        descKey: 'admin.teamDesc',
        href: '/admin/equipe',
        icon: Users,
        color: 'bg-indigo-500',
      },
      {
        key: 'users',
        labelKey: 'admin.users',
        descKey: 'admin.usersDesc',
        href: '/admin/users',
        icon: Users,
        color: 'bg-purple-500',
      },
      {
        key: 'invites',
        labelKey: 'admin.invites',
        descKey: 'admin.invitesDesc',
        href: '/admin/invites',
        icon: UserCheck,
        color: 'bg-teal-500',
      },
    ],
  },
  {
    key: 'system',
    labelKey: 'admin.navGroups.system',
    icon: Globe,
    items: [
      {
        key: 'settings',
        labelKey: 'admin.settings',
        descKey: 'admin.settingsDesc',
        href: '/admin/settings',
        icon: Globe,
        color: 'bg-orange-500',
      },
      {
        key: 'passwordReset',
        labelKey: 'admin.passwordReset',
        descKey: 'admin.passwordResetDesc',
        href: '/admin/password-reset',
        icon: Shield,
        color: 'bg-red-500',
        requiredRole: 'super_admin',
      },
    ],
  },
];

export function findActiveGroupKey(pathname: string): string | undefined {
  return ADMIN_NAV_GROUPS.find((group) =>
    group.items.some((item) => (item.match ? item.match(pathname) : pathname.startsWith(item.href)))
  )?.key;
}

export function isNavItemActive(item: AdminNavItem, pathname: string): boolean {
  return item.match ? item.match(pathname) : pathname.startsWith(item.href);
}
